import { PREFILL_CART_MAX_LIMIT } from '../consts/defaultConfigValues/defaultConstants'
import { isUsableLimit } from './recommendationLimits'

/**
 * How `globals.prefillCart.limit` reaches the `prefillCart` query, and how a
 * bad one is reported.
 *
 * Nothing is sent unless `sendLimit` is literally `true`: configs that predate
 * the flag may already author a `limit` that was never sent, and they must
 * keep sending the request they always have.
 *
 * With the flag on, the value is sent as the GraphQL `$limit: Int`. Only an
 * integer from 1 is sent; anything above `PREFILL_CART_MAX_LIMIT` is sent as
 * that ceiling. Every other authored value is **rejected** — no `limit` is
 * sent and the resolver applies its own default — rather than repaired:
 * - a quoted `"5"` would fail variable coercion and lose the whole modal;
 * - `0` is a legal `Int` that asks for nothing;
 * - an explicit `null` would reach the resolver as `null` (Apollo's `compact`
 *   strips only `undefined`), which its argument validation
 *   (`joi.number().integer()`) refuses, failing the whole query — and past
 *   that, the destructuring default in `parseProducts` does not apply to
 *   `null` and `slice(0, null)` keeps nothing;
 * - guessing what a `2.5` meant is not this client's call.
 *
 * The resolver's own default is `PARSE_PRODUCTS_LIMIT` in the storefront repo
 * (`packages/graphql-schemas/src/user/lib/storefront/helpers/ml.ts`).
 *
 * Reads `unknown` on purpose: the config is remote JSON cast to `IConfig`, so
 * at runtime these can be anything. Same reasoning as `recommendationLimits`.
 */
export interface PrefillCartLimitConfig {
  sendLimit?: unknown
  limit?: unknown
}

/**
 * The `limit` to send, or `undefined` to send none.
 *
 * `undefined` when the flag is off, the value is omitted, or it is rejected,
 * so a storefront that has not opted in sends exactly the request it always
 * has.
 */
export const resolvePrefillCartLimit = (
  prefillCart?: PrefillCartLimitConfig,
): number | undefined => {
  if (prefillCart?.sendLimit !== true) return undefined
  const { limit } = prefillCart
  return isUsableLimit(limit)
    ? Math.min(limit, PREFILL_CART_MAX_LIMIT)
    : undefined
}

export type PrefillCartLimitIssue =
  | 'clamped'
  | 'rejected'
  | 'flagNotBoolean'
  | 'missingLimit'

/**
 * What, if anything, `resolvePrefillCartLimit` did with an authored block that
 * a storefront would not expect.
 *
 * - `flagNotBoolean`: `sendLimit` is authored but is not a boolean — a quoted
 *   `"true"`, a `1` — so it reads as off and nothing is sent. Safe to report
 *   unconditionally because no config that predates the flag can carry it.
 * - `missingLimit`: the flag is on but no `limit` is authored, so nothing is
 *   sent.
 * - `rejected` / `clamped`: the flag is on and `limit` is not sendable, or is
 *   above the ceiling.
 *
 * `null` when the flag is absent or `false` — whatever `limit` holds, since an
 * old config may carry one that was never sent — and when the flag is on with
 * a sendable `limit`. An explicit `limit: null` *is* reported: cinnamon's own
 * merge never produces one, so it was written by hand.
 *
 * **Feed this the authored config**, as with
 * `isRecommendationLimitsMisconfigured`. The processed config carries
 * `defaultGlobals` underneath, and a default ever shipped there would make an
 * omitted key indistinguishable from an authored one.
 */
export const getPrefillCartLimitIssue = (
  prefillCart?: PrefillCartLimitConfig,
): PrefillCartLimitIssue | null => {
  const sendLimit = prefillCart?.sendLimit
  if (sendLimit !== undefined && typeof sendLimit !== 'boolean') {
    return 'flagNotBoolean'
  }
  if (sendLimit !== true) return null
  const { limit } = prefillCart ?? {}
  if (limit === undefined) return 'missingLimit'
  if (!isUsableLimit(limit)) return 'rejected'
  return limit > PREFILL_CART_MAX_LIMIT ? 'clamped' : null
}
